import { AnalyticsSurface, trackEvent } from './analytics'

export type BookingFlow = 'new' | 'legacy' | 'quickquote' | 'mini' | 'amend'

type RawVehicleQuote = {
  supplierQuoteReference?: string
  vehicleTypeName?: string
  vehicleDetails?: { vehicleTypeName?: string; displayName?: string }
  price?: any
  estimatedPrice?: any
  estimatedTotal?: any
  estimatedPriceCurrency?: string
  eta?: number
  isPreferredSupplier?: boolean
  isDestinationLogicQuote?: boolean
}

const pickPriceNumber = (v: any): number | undefined => {
  if (v == null) return undefined
  if (typeof v === 'number') return Number.isFinite(v) ? Number(v.toFixed(2)) : undefined
  if (typeof v?.price === 'number') return Number(v.price.toFixed(2))
  if (typeof v?.total === 'number') return Number(v.total.toFixed(2))
  return undefined
}

const pickVehicleType = (q: RawVehicleQuote): string | undefined =>
  q?.vehicleDetails?.vehicleTypeName ?? q?.vehicleTypeName

const pickCurrency = (q: RawVehicleQuote): string | undefined =>
  q?.estimatedPriceCurrency ??
  q?.price?.currency ??
  q?.estimatedTotal?.currency ??
  q?.estimatedPrice?.currency

export const flattenGroupedPrices = (
  grouped: Record<string, RawVehicleQuote[]> | null | undefined,
): RawVehicleQuote[] => (grouped ? Object.values(grouped).flat() : [])

export const fireQuotesShown = (input: {
  surface: AnalyticsSurface
  quoteGroupId?: string
  quoteGroupIdInbound?: string
  journeyType?: string
  bookingType?: string
  passengers?: number
  quotes: RawVehicleQuote[]
}) => {
  const prices = input.quotes
    .map((q) => pickPriceNumber(q?.price ?? q?.estimatedTotal ?? q?.estimatedPrice))
    .filter((n): n is number => typeof n === 'number')

  const preferredCount = input.quotes.filter((q) => q?.isPreferredSupplier).length
  const vehicleTypes = Array.from(
    new Set(
      input.quotes
        .map((q) => pickVehicleType(q))
        .filter((v): v is string => Boolean(v)),
    ),
  )

  trackEvent('quotes_shown', {
    surface: input.surface,
    quote_reference: input.quoteGroupId,
    quote_reference_inbound: input.quoteGroupIdInbound,
    journey_type: input.journeyType,
    booking_type: input.bookingType,
    passenger_count: input.passengers,
    quote_count: input.quotes.length,
    selected_vehicle_count: vehicleTypes.length,
    vehicle_types: vehicleTypes.join(','),
    preferred_supplier_available: preferredCount > 0,
    preferred_count: preferredCount,
    min_price: prices.length ? Math.min(...prices) : undefined,
    max_price: prices.length ? Math.max(...prices) : undefined,
    currency: pickCurrency(input.quotes[0] || {}),
  })
}

export const fireQuoteSelected = (input: {
  surface: AnalyticsSurface
  quoteGroupId?: string
  selectedQuote: RawVehicleQuote
  groupQuotes?: RawVehicleQuote[]
  journeyType?: string
}) => {
  const selPrice = pickPriceNumber(
    input.selectedQuote?.price ??
      input.selectedQuote?.estimatedTotal ??
      input.selectedQuote?.estimatedPrice,
  )
  const groupPrices = (input.groupQuotes || [])
    .map((q) => pickPriceNumber(q?.price ?? q?.estimatedTotal ?? q?.estimatedPrice))
    .filter((n): n is number => typeof n === 'number')
  const minInGroup = groupPrices.length ? Math.min(...groupPrices) : undefined
  const preferredInGroup = (input.groupQuotes || []).some(
    (q) => q?.isPreferredSupplier,
  )

  trackEvent('quote_selected', {
    surface: input.surface,
    quote_reference: input.quoteGroupId,
    supplier_quote_reference: input.selectedQuote?.supplierQuoteReference,
    vehicle_type: pickVehicleType(input.selectedQuote),
    selected_price: selPrice,
    currency: pickCurrency(input.selectedQuote),
    preferred_supplier: Boolean(input.selectedQuote?.isPreferredSupplier),
    destination_logic: Boolean(input.selectedQuote?.isDestinationLogicQuote),
    eta_minutes: input.selectedQuote?.eta,
    quotes_in_group: input.groupQuotes?.length,
    preferred_in_group: preferredInGroup,
    min_price_in_group: minInGroup,
    price_premium_vs_cheapest_in_group:
      typeof selPrice === 'number' && typeof minInGroup === 'number'
        ? Number((selPrice - minInGroup).toFixed(2))
        : undefined,
    journey_type: input.journeyType,
  })
}

export const fireBookingCreated = (input: {
  surface: AnalyticsSurface
  bookingId?: string
  bookingReference?: string
  quoteGroupId?: string
  quoteGroupIdInbound?: string
  supplierQuoteReference?: string
  vehicleType?: string
  winningPrice?: number
  winningPriceExVat?: number
  currency?: string
  isPreferredSupplier?: boolean
  journeyType?: string
  bookingType?: string
}) => {
  trackEvent('booking_created', {
    surface: input.surface,
    booking_id: input.bookingId,
    booking_reference: input.bookingReference,
    quote_reference: input.quoteGroupId,
    quote_reference_inbound: input.quoteGroupIdInbound,
    supplier_quote_reference: input.supplierQuoteReference,
    vehicle_type: input.vehicleType,
    winning_price: input.winningPrice,
    winning_price_ex_vat: input.winningPriceExVat,
    currency: input.currency,
    preferred_supplier: input.isPreferredSupplier,
    journey_type: input.journeyType,
    booking_type: input.bookingType,
  })
}

export const fireStartBookingProcess = (input: {
  flow: BookingFlow
  bookingType?: string
  journeyType?: string
  copiedBooking?: boolean
}) => {
  trackEvent('start_booking_process', {
    flow: input.flow,
    booking_type: input.bookingType,
    journey_type: input.journeyType,
    copied_booking: input.copiedBooking,
  })
}

const round2 = (v: any): number | undefined => {
  const n =
    typeof v === 'number'
      ? v
      : typeof v === 'string' && v.length
      ? parseFloat(v)
      : NaN
  return Number.isFinite(n) ? Number(n.toFixed(2)) : undefined
}

const priceDeltaParams = (
  previousPrice?: number,
  newPrice?: number,
  currency?: string,
) => {
  const prev = round2(previousPrice)
  const next = round2(newPrice)
  if (prev == null || next == null) {
    return {
      previous_price: prev,
      new_price: next,
      currency,
    }
  }
  const delta = Number((next - prev).toFixed(2))
  return {
    previous_price: prev,
    new_price: next,
    price_delta: delta,
    price_uplift: delta > 0 ? delta : 0,
    price_reduction: delta < 0 ? Math.abs(delta) : 0,
    value: delta,
    currency,
  }
}

export const fireBookingAmended = (input: {
  bookingId?: string | number
  bookingReference?: string
  dirtyFieldKeys?: string[]
  changedPassenger?: boolean
  changedFlight?: boolean
  changedInstructions?: boolean
  changedVehicle?: boolean
  previousPrice?: number
  newPrice?: number
  currency?: string
}) => {
  trackEvent('booking_amended', {
    surface: 'amend' as AnalyticsSurface,
    booking_id: input.bookingId,
    booking_reference: input.bookingReference,
    changed_fields: input.dirtyFieldKeys?.join(','),
    changed_field_count: input.dirtyFieldKeys?.length,
    changed_passenger: input.changedPassenger,
    changed_flight: input.changedFlight,
    changed_instructions: input.changedInstructions,
    changed_vehicle: input.changedVehicle,
    ...priceDeltaParams(input.previousPrice, input.newPrice, input.currency),
  })
}

export const fireJourneyReallocated = (input: {
  bookingVehicleId?: string
  quoteGroupId?: string
  toSupplierQuoteReference?: string
  vehicleType?: string
  price?: number
  previousPrice?: number
  currency?: string
  isPreferredSupplier?: boolean
}) => {
  trackEvent('journey_reallocated', {
    surface: 'reallocate' as AnalyticsSurface,
    booking_vehicle_id: input.bookingVehicleId,
    quote_reference: input.quoteGroupId,
    to_supplier_quote_reference: input.toSupplierQuoteReference,
    vehicle_type: input.vehicleType,
    price: round2(input.price),
    preferred_supplier: input.isPreferredSupplier,
    ...priceDeltaParams(input.previousPrice, input.price, input.currency),
  })
}
